/* marquee.js — <onbond-marquee> web component + legacy marquee support */

class OnbondMarquee extends HTMLElement {
  connectedCallback() {
    const extras = (this.dataset.extra || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const injectVendors = this.dataset.injectVendors === 'true';
    const track = this.querySelector('[data-track]');
    if (!track) return;

    if (injectVendors) {
      this._fetchVendors().then(vendors => {
        const extraItems  = extras.map(t => ({ text: t, url: null }));
        const vendorItems = vendors.map(v => ({ text: v, url: this._vendorUrl(v) }));
        let items = [...extraItems, ...vendorItems];
        if (!items.length) items = this._fallback();
        this._buildTrack(track, items);
        this._initDrag(track);
      });
    } else {
      const items = extras.length
        ? extras.map(t => ({ text: t, url: null }))
        : this._fallback();
      this._buildTrack(track, items);
      this._initDrag(track);
    }
  }

  _vendorUrl(vendor) {
    const handle = vendor.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
    return `/collections/all?filter.p.vendor=${encodeURIComponent(vendor)}`;
  }

  _fallback() {
    return [
      { text: 'Authenticated archive', url: null },
      { text: 'Worldwide shipping',    url: null },
      { text: 'New arrivals weekly',   url: null },
      { text: 'Yohji Yamamoto',        url: this._vendorUrl('Yohji Yamamoto') },
      { text: 'Comme des Garcons',     url: this._vendorUrl('Comme des Garcons') },
      { text: 'Helmut Lang',           url: this._vendorUrl('Helmut Lang') },
      { text: 'Maison Margiela',       url: this._vendorUrl('Maison Margiela') },
      { text: 'Issey Miyake',          url: this._vendorUrl('Issey Miyake') },
      { text: 'Yves Saint Laurent',    url: this._vendorUrl('Yves Saint Laurent') },
      { text: 'Number Nine',           url: this._vendorUrl('Number Nine') },
      { text: 'Raf Simons',            url: this._vendorUrl('Raf Simons') },
    ];
  }

  async _fetchVendors() {
    const vendorCount = {};
    try {
      const r = await fetch('/collections/all/products.json?limit=250&page=1');
      if (r.ok) {
        const { products } = await r.json();
        (products || []).forEach(p => {
          if (p.vendor) vendorCount[p.vendor] = (vendorCount[p.vendor] || 0) + 1;
        });
      }
    } catch { /* silent */ }
    return Object.entries(vendorCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([v]) => v);
  }

  _buildTrack(track, items) {
    const sep = '<span class="onbond-marquee__sep">&#10022;</span>';
    const markup = items.map(item => {
      const inner = `<span class="onbond-marquee__item label-mono">${item.text || item}</span>`;
      return item.url
        ? `<a href="${item.url}" class="onbond-marquee__link">${inner}</a>${sep}`
        : `${inner}${sep}`;
    }).join('');
    track.innerHTML = markup + markup;
    const hw = track.scrollWidth / 2;
    if (hw) track.style.setProperty('--mq-travel', `-${hw}px`);
    track.style.animation = 'onbond-marquee 18s linear infinite';
  }

  _initDrag(track) {
    let dragging = false;
    let didDrag  = false;
    let startX = 0;
    let currentX = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let rafId = null;
    let activeId = null;
    let downX = 0;
    let downY = 0;
    let intentLocked = false;
    let isHorizontal = false;

    const getHalfWidth = () => track.scrollWidth / 2;

    const freezeAnim = () => {
      const style = window.getComputedStyle(track);
      const transform = style.transform;
      if (transform && transform !== 'none') {
        const m = transform.match(/matrix.*\((.+)\)/);
        if (m) currentX = parseFloat(m[1].split(', ')[4]) || 0;
      }
      track.style.animation = 'none';
      track.style.transform = `translate3d(${currentX}px,0,0)`;
    };

    const DURATION = 18;
    const resumeAnim = () => {
      const hw = getHalfWidth();
      let delay = 0;
      if (hw) {
        track.style.setProperty('--mq-travel', `-${hw}px`);
        currentX = -(((-currentX) % hw + hw) % hw);
        delay = -((-currentX / hw) * DURATION);
      }
      track.style.transform = '';
      track.style.animation = `onbond-marquee ${DURATION}s linear ${delay}s infinite`;
    };

    const wrap = () => {
      const hw = getHalfWidth();
      if (!hw) return;
      if (currentX <= -hw) currentX += hw;
      else if (currentX >= 0) currentX -= hw;
    };

    track.addEventListener('pointerdown', (e) => {
      if (activeId !== null) return;
      cancelAnimationFrame(rafId);
      freezeAnim();
      dragging = true;
      didDrag  = false;
      intentLocked = false;
      isHorizontal = false;
      activeId = e.pointerId;
      downX    = e.clientX;
      downY    = e.clientY;
      startX   = e.clientX - currentX;
      lastX    = e.clientX;
      lastT    = Date.now();
      velocity = 0;
      track.style.cursor = 'grabbing';
    });

    window.addEventListener('pointermove', (e) => {
      if (!dragging || e.pointerId !== activeId) return;

      if (!intentLocked) {
        const dx = Math.abs(e.clientX - downX);
        const dy = Math.abs(e.clientY - downY);
        if (dx > 4 || dy > 4) {
          isHorizontal = dx >= dy;
          intentLocked = true;
        }
      }

      if (!isHorizontal) {
        dragging = false;
        activeId = null;
        track.style.cursor = '';
        resumeAnim();
        return;
      }

      e.preventDefault();
      const now = Date.now();
      const dt = now - lastT;
      if (dt > 10) {
        velocity = velocity * 0.5 + ((e.clientX - lastX) / dt) * 0.5;
        lastX = e.clientX;
        lastT = now;
      }
      if (Math.abs(e.clientX - downX) > 6) didDrag = true;
      currentX = e.clientX - startX;
      wrap();
      track.style.transform = `translate3d(${currentX}px,0,0)`;
    }, { passive: false });

    window.addEventListener('pointerup', (e) => {
      if (!dragging || e.pointerId !== activeId) return;
      dragging = false;
      activeId = null;
      track.style.cursor = '';

      if (!didDrag) {
        resumeAnim();
        return;
      }

      let lastTime = Date.now();
      const inertia = () => {
        const now = Date.now();
        const dt = now - lastTime;
        lastTime = now;
        velocity *= Math.pow(0.5, dt / 250);
        currentX += velocity * dt;
        wrap();
        track.style.transform = `translate3d(${currentX}px,0,0)`;
        if (Math.abs(velocity) > 0.02) {
          rafId = requestAnimationFrame(inertia);
        } else {
          resumeAnim();
        }
      };
      rafId = requestAnimationFrame(inertia);
    });

    window.addEventListener('pointercancel', (e) => {
      if (e.pointerId !== activeId) return;
      dragging = false;
      activeId = null;
      resumeAnim();
    });

    /* Suppress click after a drag; let native <a> clicks work normally on taps */
    track.addEventListener('click', (e) => {
      if (didDrag) e.preventDefault();
    });
  }
}

if (!customElements.get('onbond-marquee')) {
  customElements.define('onbond-marquee', OnbondMarquee);
}
